import { VpAddIcon } from '@vtmn-play/icons/react';
import {
  VpButton,
  VpFormControl,
  VpFormLabel,
  VpSelect,
  VpSelectOption,
} from '@vtmn-play/react/headless';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormCreateUserNeedResponse } from '@spo/types';
import './user-need-segmentation.scss';

type SegmentationsValue = {
  id: number;
  name: string;
  status: string;
  order: number;
  typeId: number;
};

export type SegmentationsType = { id: number; label: string };

export type UserNeedSegmentationProps = {
  formCreateUserNeedData: FormCreateUserNeedResponse;
  setUnSegmentationType: (id: number) => void;
  setUnSegmentationValue: (id: number) => void;
};

export const UserNeedSegmentations = ({
  formCreateUserNeedData,
  setUnSegmentationType,
  setUnSegmentationValue,
}: UserNeedSegmentationProps) => {
  const [showSegmentationInputs, setShowSegmentationInputs] = useState(false);
  const [associatedSegValues, setAssociatedSegValues] = useState<
    SegmentationsValue[]
  >([]);
  const [segmentationType, setSegmentationType] =
    useState<SegmentationsType | null>(null);
  const [segmentationValue, setSegmentationValue] =
    useState<SegmentationsType | null>(null);
  const { t } = useTranslation();

  const flatSegmentationsTypes =
    formCreateUserNeedData.usageSegmentations?.flatMap(
      (seg) => seg.usageSegmentationType,
    );

  const updateSegType = (segLabel: string) => {
    const chosenSegType = formCreateUserNeedData.usageSegmentations?.find(
      (seg) => seg.usageSegmentationType.label === segLabel.split(' - ')[1],
    );
    if (chosenSegType) {
      setSegmentationType(chosenSegType.usageSegmentationType);
      setUnSegmentationType(chosenSegType.usageSegmentationType.id);
    }
    const associatedValues = chosenSegType?.usageSegmentationValues;
    if (associatedValues) {
      setAssociatedSegValues(associatedValues);
    }
  };

  const updateSegValue = (segValue: string) => {
    const chosenValue = associatedSegValues.find(
      (value) => value.name === segValue.split(' - ')[1],
    );
    if (chosenValue) {
      setSegmentationValue({
        id: chosenValue.id,
        label: chosenValue.name,
      });
    }
    if (segmentationType) {
      setUnSegmentationType(segmentationType.id);
    }
    if (chosenValue) {
      setUnSegmentationValue(chosenValue.id);
    }
  };

  return showSegmentationInputs ? (
    <div
      className="user-need-segmentation"
      data-testid="user-need_segmentation"
    >
      <VpFormControl>
        <VpFormLabel className="user-need-form-labels">
          {t('architecture.drawer.user-need.segmentation-type')}
        </VpFormLabel>
        <VpSelect
          id="un-seg-type-select"
          name="un-seg-type-select"
          data-testid="user-need_segmentation-type_select"
          placeholder={t(
            'architecture.drawer.user-need.segmentation-type-placeholder',
          )}
          className="un-seg-type-select"
          defaultValue={segmentationType?.label || ''}
          onChange={(e) => updateSegType(e.target.value)}
        >
          {flatSegmentationsTypes?.map((segType) => (
            <VpSelectOption
              key={segType.id}
            >{`${segType.id} - ${segType.label}`}</VpSelectOption>
          ))}
        </VpSelect>
      </VpFormControl>
      <VpFormControl disabled={!segmentationType?.label}>
        <VpFormLabel className="user-need-form-labels">
          {t('architecture.drawer.user-need.segmentation-value')}
        </VpFormLabel>
        <VpSelect
          id="un-seg-value-select"
          name="un-seg-value-select"
          data-testid="user-need_segmentation-value_select"
          placeholder={t(
            'architecture.drawer.user-need.segmentation-value-placeholder',
          )}
          className="un-seg-value-select"
          defaultValue={segmentationValue?.label || ''}
          onChange={(e) => updateSegValue(e.target.value)}
        >
          {associatedSegValues.map((segValue) => (
            <VpSelectOption
              key={segValue.id}
            >{`${segValue.id} - ${segValue.name}`}</VpSelectOption>
          ))}
        </VpSelect>
      </VpFormControl>
    </div>
  ) : (
    <div className="user-need-seg-button-wrapper">
      {formCreateUserNeedData.showAddSegmentationButton ? (
        <VpButton
          data-testid="user-need_add_segmentation_button"
          className="user-need-add-seg-button"
          shape="squared"
          variant="secondary"
          onClick={() => setShowSegmentationInputs(true)}
        >
          <VpAddIcon />
          {t('architecture.drawer.user-need.add-segmentation')}
        </VpButton>
      ) : (
        <div className="user-need-no-usage-segmentation">
          {t('architecture.drawer.settings.no-usage-seg-type')}
        </div>
      )}
    </div>
  );
};
